// src/pages/Appli/AppliAdmin/VisitorCenter/components/SessionsPanel.jsx

import React from "react";

import {
  FiClock,
  FiFilter,
  FiRefreshCw,
  FiSearch,
  FiX,
} from "react-icons/fi";

import {
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  SectionCard,
  StatusBadge,
} from "../ui/VisitorCenterUi";

import {
  formatDateTime,
  formatLocation,
  formatPageLabel,
  formatTrafficSource,
} from "../utils/visitorFormatters";

function FilterLabel({
  label,
  children,
  className = "",
}) {
  return (
    <label
      className={`block ${className}`}
    >
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-slate-400">
        {label}
      </span>

      {children}
    </label>
  );
}

export default function SessionsPanel({
  sessions,
  filters,

  page,
  totalPages,
  count,

  loading,
  errorMessage,

  selectedSession,
  detailsLoading,

  onFilterChange,
  onPageChange,
  onSelectSession,

  detailsComponent,
}) {
  const hasActiveFilters =
    Boolean(filters.path) ||
    Boolean(filters.source) ||
    Boolean(filters.status) ||
    Boolean(filters.device_type) ||
    Boolean(filters.date_from) ||
    Boolean(filters.date_to);

  const resetFilters = () => {
    onFilterChange("path", "");
    onFilterChange("source", "");
    onFilterChange("status", "");
    onFilterChange(
      "device_type",
      "",
    );
    onFilterChange(
      "date_from",
      "",
    );
    onFilterChange("date_to", "");
  };

  return (
    <div className="grid gap-6 2xl:grid-cols-[minmax(0,1.2fr)_minmax(390px,0.8fr)]">
      <SectionCard
        title="Historique des sessions"
        subtitle={`${count} session${
          count > 1 ? "s" : ""
        } enregistrée${
          count > 1 ? "s" : ""
        }`}
        action={
          hasActiveFilters ? (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
            >
              <FiX />
              Effacer les filtres
            </button>
          ) : null
        }
      >
        {/* Filtres */}
        <div className="border-b border-slate-100 bg-slate-50/60 p-4 lg:p-5">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EEF7FC] text-[#2EA7E0]">
              <FiFilter />
            </div>

            <div>
              <div className="font-extrabold text-[#123B57]">
                Filtrer les sessions
              </div>

              <div className="text-xs text-slate-500">
                La recherche de page porte sur tout le parcours du visiteur.
              </div>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
            <FilterLabel
              label="Page visitée"
              className="md:col-span-2 xl:col-span-2"
            >
              <div className="relative">
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                <input
                  value={filters.path}
                  onChange={(event) =>
                    onFilterChange(
                      "path",
                      event.target.value,
                    )
                  }
                  placeholder="Accueil, tarifs, contact..."
                  className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-[#2EA7E0] focus:ring-2 focus:ring-[#2EA7E0]/10"
                />
              </div>
            </FilterLabel>

            <FilterLabel label="Source">
              <input
                value={filters.source}
                onChange={(event) =>
                  onFilterChange(
                    "source",
                    event.target.value,
                  )
                }
                placeholder="Google, Facebook..."
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-[#2EA7E0] focus:ring-2 focus:ring-[#2EA7E0]/10"
              />
            </FilterLabel>

            <FilterLabel label="Statut">
              <select
                value={filters.status}
                onChange={(event) =>
                  onFilterChange(
                    "status",
                    event.target.value,
                  )
                }
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-[#2EA7E0] focus:ring-2 focus:ring-[#2EA7E0]/10"
              >
                <option value="">
                  Tous les statuts
                </option>

                <option value="online">
                  En ligne
                </option>

                <option value="away">
                  Temporairement absent
                </option>

                <option value="offline">
                  Hors ligne
                </option>
              </select>
            </FilterLabel>

            <FilterLabel label="Appareil">
              <select
                value={
                  filters.device_type
                }
                onChange={(event) =>
                  onFilterChange(
                    "device_type",
                    event.target.value,
                  )
                }
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-[#2EA7E0] focus:ring-2 focus:ring-[#2EA7E0]/10"
              >
                <option value="">
                  Tous les appareils
                </option>

                <option value="desktop">
                  Ordinateur
                </option>

                <option value="mobile">
                  Mobile
                </option>

                <option value="tablet">
                  Tablette
                </option>
              </select>
            </FilterLabel>

            <FilterLabel label="Robots">
              <select
                value={
                  filters.exclude_bots
                }
                onChange={(event) =>
                  onFilterChange(
                    "exclude_bots",
                    event.target.value,
                  )
                }
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-[#2EA7E0] focus:ring-2 focus:ring-[#2EA7E0]/10"
              >
                <option value="true">
                  Robots exclus
                </option>

                <option value="false">
                  Robots inclus
                </option>
              </select>
            </FilterLabel>

            <FilterLabel
              label="Du"
              className="md:col-span-1 xl:col-span-2"
            >
              <input
                type="date"
                value={
                  filters.date_from
                }
                onChange={(event) =>
                  onFilterChange(
                    "date_from",
                    event.target.value,
                  )
                }
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-[#2EA7E0] focus:ring-2 focus:ring-[#2EA7E0]/10"
              />
            </FilterLabel>

            <FilterLabel
              label="Au"
              className="md:col-span-1 xl:col-span-2"
            >
              <input
                type="date"
                value={filters.date_to}
                onChange={(event) =>
                  onFilterChange(
                    "date_to",
                    event.target.value,
                  )
                }
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-[#2EA7E0] focus:ring-2 focus:ring-[#2EA7E0]/10"
              />
            </FilterLabel>
          </div>
        </div>

        <ErrorState
          message={errorMessage}
        />

        {loading ? (
          <LoadingState label="Chargement des sessions..." />
        ) : sessions.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-[#F8FBFD] text-left text-xs uppercase tracking-[0.1em] text-slate-400">
                <tr>
                  <th className="whitespace-nowrap px-5 py-4">
                    Visiteur
                  </th>

                  <th className="whitespace-nowrap px-5 py-4">
                    Entrée
                  </th>

                  <th className="whitespace-nowrap px-5 py-4">
                    Page actuelle
                  </th>

                  <th className="whitespace-nowrap px-5 py-4">
                    Source
                  </th>

                  <th className="whitespace-nowrap px-5 py-4">
                    Appareil
                  </th>

                  <th className="whitespace-nowrap px-5 py-4 text-center">
                    Pages
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {sessions.map(
                  (session) => {
                    const active =
                      Number(
                        selectedSession?.id,
                      ) ===
                      Number(session.id);

                    return (
                      <tr
                        key={
                          session.id
                        }
                        onClick={() =>
                          onSelectSession(
                            session.id,
                          )
                        }
                        className={`cursor-pointer transition ${
                          active
                            ? "bg-[#EEF7FC]"
                            : "hover:bg-[#F8FBFD]"
                        }`}
                      >
                        <td className="px-5 py-4">
                          <div className="font-extrabold text-[#123B57]">
                            Visiteur #
                            {session.id}
                          </div>

                          <div className="mt-1.5">
                            <StatusBadge
                              status={
                                session.status
                              }
                            />
                          </div>
                        </td>

                        <td className="min-w-[190px] px-5 py-4 text-sm text-slate-600">
                          <div className="font-semibold">
                            {formatDateTime(
                              session.first_seen_at,
                            )}
                          </div>

                          <div className="mt-1 text-xs text-slate-400">
                            {formatLocation(
                              session,
                            )}
                          </div>
                        </td>

                        <td className="max-w-[280px] px-5 py-4">
                          <div className="truncate text-sm font-bold text-slate-700">
                            {formatPageLabel(
                              session.current_page,
                            )}
                          </div>

                          <div className="mt-1 max-w-[260px] truncate text-xs text-slate-400">
                            {session.current_page ||
                              "Page inconnue"}
                          </div>
                        </td>

                        <td className="min-w-[150px] px-5 py-4 text-sm text-slate-600">
                          {formatTrafficSource(
                            session,
                          )}
                        </td>

                        <td className="min-w-[150px] px-5 py-4 text-sm text-slate-600">
                          <div className="font-semibold capitalize">
                            {session.device_type ||
                              "Inconnu"}
                          </div>

                          <div className="mt-1 text-xs text-slate-400">
                            {[
                              session.browser_name,
                              session.os_name,
                            ]
                              .filter(
                                Boolean,
                              )
                              .join(" · ") ||
                              "Informations inconnues"}
                          </div>
                        </td>

                        <td className="px-5 py-4 text-center">
                          <span className="inline-flex min-w-9 items-center justify-center rounded-xl bg-[#EEF7FC] px-2.5 py-2 font-extrabold text-[#123B57]">
                            {Number(
                              session.page_views_count,
                            ) || 0}
                          </span>
                        </td>
                      </tr>
                    );
                  },
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={FiClock}
            title="Aucune session trouvée"
            description="Modifiez les filtres ou attendez l’enregistrement de nouvelles visites."
          />
        )}

        <Pagination
          page={page}
          totalPages={totalPages}
          onChange={onPageChange}
        />
      </SectionCard>

      <SectionCard
        title="Détail de la session"
        subtitle={
          selectedSession
            ? `Parcours du visiteur #${selectedSession.id}`
            : "Sélectionnez une session dans le tableau"
        }
        action={
          detailsLoading ? (
            <div className="inline-flex items-center gap-2 rounded-xl bg-[#EEF7FC] px-3 py-2 text-xs font-bold text-[#1C567F]">
              <FiRefreshCw className="animate-spin" />
              Chargement
            </div>
          ) : null
        }
      >
        {React.cloneElement(
          detailsComponent,
          {
            visitor:
              selectedSession,

            loading:
              detailsLoading,
          },
        )}
      </SectionCard>
    </div>
  );
}